import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Layout, message, Modal, Popconfirm, Row, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { doc, getDoc, updateDoc, writeBatch, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const generateId = () => Math.random().toString(36).substring(2, 10);

const OrchestraSettings = () => {
    const { currentEventId } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (currentEventId) {
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEventId]);

    const fetchEventData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setSessions(data.orchestraSessions || []);
                setVenues(data.venues || []);
            } else {
                message.error('Event not found');
            }
        } catch (error) {
            console.error('Error fetching event data:', error);
            message.error('Failed to fetch event data');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);
            const newSession = {
                id: editingSession ? editingSession.id : generateId(),
                venue: values.venue,
                date: values.date.format('YYYY-MM-DD'),
                time: values.time,
                reservedRows: values.reservedRows || [],
                complimentaryQuota: values.complimentaryQuota || 0,
                complimentaryClaimed: editingSession ? (editingSession.complimentaryClaimed || 0) : 0,
            };

            const sessionId = `${newSession.date}_${newSession.time}`;

            // Seat generation logic for NEW sessions
            if (!editingSession) {
                const selectedVenue = venues.find(v => v.id === newSession.venue);
                if (selectedVenue && selectedVenue.seatConfig && selectedVenue.seatConfig.length > 0) {
                    const batch = writeBatch(db);
                    const seatsRef = collection(db, `seats${currentEventId}`);
                    
                    selectedVenue.seatConfig.forEach(config => {
                        for (let i = 1; i <= config.seatCount; i++) {
                            const newSeatRef = doc(seatsRef);
                            batch.set(newSeatRef, {
                                venueId: newSession.venue,
                                sessionId: sessionId,
                                row: config.row,
                                number: i,
                                areaType: config.areaType,
                                seatLabel: `${config.row}-${i}`,
                                status: 'available'
                            });
                        }
                    });
                    
                    await batch.commit();
                    message.success(`Generated seats for new session.`);
                }
            }


            let updatedSessions;
            if (editingSession) {
                updatedSessions = sessions.map(s => s.id === editingSession.id ? newSession : s);
            } else {
                updatedSessions = [...sessions, newSession];
            }

            await updateDoc(docRef, { orchestraSessions: updatedSessions });
            message.success('Orchestra session saved successfully');
            setSessions(updatedSessions);
            setIsModalVisible(false);
        } catch (error) {
            console.error('Error saving orchestra session:', error);
            message.error('Failed to save session');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const docRef = doc(db, 'events', currentEventId);
            const updatedSessions = sessions.filter(s => s.id !== id);
            await updateDoc(docRef, { orchestraSessions: updatedSessions });
            message.success('Session deleted successfully');
            setSessions(updatedSessions);
        } catch (error) {
            console.error('Error deleting session:', error);
            message.error('Failed to delete session');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (session = null) => {
        setEditingSession(session);
        if (session) {
            form.setFieldsValue({
                ...session,
                date: dayjs(session.date),
            });
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const columns = [
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            render: (venueId) => {
                const v = venues.find(v => v.id === venueId);
                return v ? v.label : venueId;
            }
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Time',
            dataIndex: 'time',
            key: 'time',
        },
        {
            title: 'Reserved Rows',
            dataIndex: 'reservedRows',
            key: 'reservedRows',
            render: (rows) => (rows || []).join(', '),
        },
        {
            title: 'Free Tickets Quota',
            key: 'quota',
            render: (_, record) => `${record.complimentaryClaimed || 0} / ${record.complimentaryQuota || 0}`,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => openModal(record)} />
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record.id)}>
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Orchestra Sessions</Title>
                    <Text type="secondary">Manage public orchestra sessions and complimentary tickets quota for registrants.</Text>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                    Add Session
                </Button>
            </div>

            <Card style={{ marginTop: '24px' }}>
                <Table
                    columns={columns}
                    dataSource={sessions}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                />
            </Card>

            <Modal
                title={editingSession ? 'Edit Orchestra Session' : 'Add Orchestra Session'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                <Form layout="vertical" form={form} onFinish={handleSave}>
                    <Form.Item name="venue" label="Venue" rules={[{ required: true }]}>
                        <Select placeholder="Select a venue" disabled={!!editingSession}>
                            {venues.map(v => (
                                <Option key={v.id} value={v.id}>{v.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="time" label="Time" rules={[{ required: true }]}>
                                <Input placeholder="e.g., 19:00" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="reservedRows"
                        label="Reserved Rows (For Free Tickets)"
                        extra="These rows will be blocked from public selection."
                    >
                        <Select mode="tags" placeholder="e.g., A, B, C">
                            {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(row => (
                                <Option key={row} value={row}>{row}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="complimentaryQuota"
                        label="Free Tickets Quota"
                        rules={[{ required: true }]}
                        extra="Total free tickets that can be claimed by registrants for this session."
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item style={{ textAlign: 'right', marginTop: '24px' }}>
                        <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>Save</Button>
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default OrchestraSettings;
